import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { movies, categories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const movie: MovieItem | undefined = movies.find((m) => m.slug === slug);
  const relatedMovies: MovieItem[] = movies
    .filter((m) => m.slug !== slug && (m.categorySlug === movie?.categorySlug || m.category === movie?.category))
    .slice(0, 6);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-100">
        <Navbar />
        <main className="pt-20 container-main">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-500 w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-200 mb-2">影片未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查找的影片不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-950 font-medium text-sm rounded-full whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 500000) + 100000,
    },
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - 鲁鲁影院高清免费在线观看_${movie.titleEn}_高清流畅不缓冲`}
        description={`鲁鲁影院为您提供${movie.title}(${movie.titleEn})高清免费在线观看，${movie.description.slice(0, 100)}... 导演${movie.director}，主演${movie.actors.join('、')}，高清画质流畅播放。`}
        keywords={`${movie.title},${movie.titleEn},${movie.category},高清免费,在线观看,鲁鲁影院,${movie.actors.join(',')},${movie.director}`}
        canonicalUrl={`https://lulucinema.com/article/${movie.slug}`}
        ogType="video.movie"
        structuredData={articleStructuredData}
      />

      <div className="min-h-screen bg-background-100">
        <Navbar />

        <main className="pt-16">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: movie.category, href: `/category/${movie.categorySlug}` },
                { label: movie.title },
              ]}
            />
          </div>

          <article>
            <section className="py-6 md:py-10">
              <div className="container-main">
                <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                  <div className="shrink-0 w-full md:w-[300px]">
                    <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 海报`}
                        title={`${movie.title} - 鲁鲁影院高清在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-2">
                      {movie.title}
                    </h1>
                    <p className="text-lg text-foreground-400 mb-4">{movie.titleEn}</p>

                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <span className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500/15 rounded-full">
                        <i className="ri-star-fill text-primary-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-primary-400 font-bold text-lg">{movie.rating}</span>
                      </span>
                      <span className="px-3 py-1.5 bg-background-200/70 rounded-full text-sm text-foreground-400">
                        {movie.category}
                      </span>
                      <span className="px-3 py-1.5 bg-background-200/70 rounded-full text-sm text-foreground-400">
                        {movie.year}
                      </span>
                      <span className="px-3 py-1.5 bg-background-200/70 rounded-full text-sm text-foreground-400">
                        {movie.duration}
                      </span>
                      <span className="px-3 py-1.5 bg-background-200/70 rounded-full text-sm text-foreground-400">
                        {movie.region}
                      </span>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-foreground-600 shrink-0 w-14">导演</span>
                        <span className="text-sm text-foreground-200">{movie.director}</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-foreground-600 shrink-0 w-14">主演</span>
                        <span className="text-sm text-foreground-200">{movie.actors.join(' / ')}</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-foreground-600 shrink-0 w-14">类型</span>
                        <div className="flex items-center gap-2 flex-wrap">
                          {movie.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-900 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-foreground-600 shrink-0 w-14">更新</span>
                        <span className="text-sm text-foreground-200">{movie.updateDate}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer">
                        <i className="ri-play-fill text-xl w-6 h-6 flex items-center justify-center"></i>
                        立即播放
                      </button>
                      <button className="inline-flex items-center gap-2 px-5 py-3 bg-background-200/70 hover:bg-background-300/50 text-foreground-300 text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer">
                        <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i>
                        收藏
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-8 bg-background-50">
              <div className="container-main">
                <h2 className="text-lg font-bold text-foreground-100 mb-4">
                  <a href="#plot-summary">剧情简介</a>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-4xl">
                  {movie.description}
                </p>
              </div>
            </section>

            {relatedMovies.length > 0 && (
              <section className="py-10">
                <div className="container-main">
                  <h2 className="text-lg font-bold text-foreground-100 mb-5">
                    <a href="#related-movies">相关推荐</a>
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}